import { observer } from 'mobx-react-lite';
import * as React from 'react';
import {
    addDays,
    formatShortMonthDay,
    formatShortWeekDay,
    formatWeekDay,
    getDate,
    OwaDate,
} from 'owa-datetime';
import { isPastDate, isToday } from 'owa-observable-datetime';
import { useElementSizeTracker } from 'owa-react-hooks/lib/useElementSizeTracker';
import { DateHeaderMonthNameMinWidth } from '../constants';

import styles from './CalendarDateHeader.scss';
import classnamesBind from 'classnames/bind';
let classNames = classnamesBind.bind(styles);

export interface CalendarDateHeaderProps {
    displayDate: OwaDate;
    formatHeader: (date: OwaDate) => string;
    onSurfaceClick?: (
        start: OwaDate,
        end: OwaDate,
        isAllDay: boolean,
        props?: any,
        selectedCalendarId?: string
    ) => void;
    style?: React.CSSProperties;
    calendarFolderId?: string;
    children?: React.ReactNode;
    shouldShowWeekMonthName?: () => boolean;
    hideTodayEmphasis?: boolean;
}

/**
 * This panel shows date headers for surface columns
 */
export default observer(function CalendarDateHeader(props: CalendarDateHeaderProps) {
    const {
        displayDate,
        children,
        formatHeader,
        onSurfaceClick,
        style,
        calendarFolderId,
        shouldShowWeekMonthName,
        hideTodayEmphasis,
        ...divProps
    } = props;

    const openQuickCompose = (ev?: React.MouseEvent<HTMLElement>) => {
        displayDate;
        onSurfaceClick(
            displayDate,
            addDays(displayDate, 1),
            true,
            false /* No click and drag scenario */,
            calendarFolderId
        );
    };
    const renderHeaderContent = (
        shouldShowMonthString: boolean,
        isTodayDate: boolean,
        isPast: boolean,
        hideTodayEmphasis: boolean
    ): JSX.Element => {
        let { displayDate, formatHeader } = props;
        if (shouldShowMonthString) {
            let header = [formatShortMonthDay(displayDate), formatWeekDay(displayDate)].join(' ');
            let headerStyleClassName = classNames(
                styles.monthValueInHeader,
                styles.enlargedHeader,
                isTodayDate && !hideTodayEmphasis ? styles.monthValueInHeader : styles.futureHeader,
                {
                    dayHeaderCurrentDay: isTodayDate && !hideTodayEmphasis,
                }
            );
            // text is aria-hidden so that we don't stop on it in scan mode, since the items themselves all have date information
            return (
                <div className={headerStyleClassName} aria-hidden="true">
                    <time title={header}> {formatShortMonthDay(displayDate)} </time>
                    <div className={classNames(styles.dayHeader)} title={header}>
                        {formatShortWeekDay(displayDate)}
                    </div>
                </div>
            );
        } else {
            let header = formatHeader(displayDate);
            let headerStyleClassName = classNames(
                styles.enlargedHeader,
                isTodayDate && !hideTodayEmphasis
                    ? styles.monthValueInHeader
                    : isPast
                    ? styles.pastHeader
                    : styles.futureHeader
            );
            // text is aria-hidden so that we don't stop on it in scan mode, since the items themselves all have date information
            return (
                <div className={headerStyleClassName} aria-hidden="true">
                    <time title={header}> {header} </time>
                    <div className={styles.dayHeader}>{formatShortWeekDay(displayDate)}</div>
                </div>
            );
        }
    };
    const dateHeaderRef = React.useRef<HTMLDivElement | null>(null);
    const [dateHeaderRect] = useElementSizeTracker(dateHeaderRef);
    const isPast = isPastDate(displayDate);
    const isTodayDate = isToday(displayDate);
    const shouldShowName = shouldShowWeekMonthName?.() ?? true;
    const shouldShowMonthString =
        dateHeaderRect &&
        dateHeaderRect?.width > DateHeaderMonthNameMinWidth &&
        shouldShowName &&
        (getDate(displayDate) == 1 || (isTodayDate && !hideTodayEmphasis));

    return (
        <div
            ref={dateHeaderRef}
            className={classNames(styles.header, styles.weekHeader, {
                pastDay: isPast,
                selectedHeader: isTodayDate && !hideTodayEmphasis,
                containsAddon: !!children,
            })}
            onClick={openQuickCompose}
            style={style}
            {...divProps}>
            {renderHeaderContent(shouldShowMonthString, isTodayDate, isPast, hideTodayEmphasis)}
            {children}
        </div>
    );
});
